import React from 'react';
import { OfflineQueueItem, SyncActionType } from '../domain';
import { Button, Dialog, DialogBody, DialogFooter, DialogFooterSpacer } from './ui';

const ACTION_TYPE_LABELS: Record<SyncActionType, string> = {
  ChoreCompleted: 'Mark chore done',
  ChoreCreated: 'Create chore',
  ChoreDeleted: 'Delete chore',
  ChoreUpdated: 'Update chore',
  HouseholdCreated: 'Create household',
  HouseholdDeleted: 'Delete household',
};

interface SyncIssuesDialogProps {
  open: boolean;
  items: OfflineQueueItem[];
  onRetryAll: () => void;
  onDiscardAll: () => void;
  onClose: () => void;
  onForceFullResync: () => void;
  isSyncing: boolean;
}

export const SyncIssuesDialog: React.FC<SyncIssuesDialogProps> = ({
  open,
  items,
  onRetryAll,
  onDiscardAll,
  onClose,
  onForceFullResync,
  isSyncing,
}) => {
  // A refused action is already out of the queue (isRefusedByServer): there's nothing to retry,
  // only the report to clear.
  const anyRetryable = items.some(item => !item.refused);
  return (
    <Dialog open={open} onClose={onClose} title="Sync Queue">
      <DialogBody>
        {items.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No failed actions.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {items.map((item, index) => (
              <li key={item.id ?? index} className="py-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {ACTION_TYPE_LABELS[item.type] ?? item.type}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {new Date(item.timestamp).toLocaleString()}
                </p>
                {item.lastError && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{item.lastError}</p>
                )}
                {item.refused && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    It won't be tried again. What it changed on this phone is put back as the server has it.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-950/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Not seeing a change someone else made on another device? A full resync re-fetches
            every chore from the server instead of just what's changed recently.
          </p>
          <Button size="sm" onClick={onForceFullResync} loading={isSyncing} className="mt-2">
            Force full resync
          </Button>
        </div>
      </DialogBody>

      <DialogFooter>
        {items.length > 0 && (
          <Button variant="danger-ghost" onClick={onDiscardAll}>
            {anyRetryable ? 'Discard all' : 'Clear'}
          </Button>
        )}
        <DialogFooterSpacer />
        <Button variant="ghost" onClick={onClose}>Close</Button>
        {anyRetryable && <Button variant="primary" onClick={onRetryAll}>Retry all</Button>}
      </DialogFooter>
    </Dialog>
  );
};
